import { useMemo, useState } from "react";
import {
    Alert, Button, Card, Col, Divider, InputNumber, Modal, Row, Select, Space, Statistic, Table, Tag, Upload,
} from "antd";
import { ExclamationCircleOutlined, UploadOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";

import axiosInstance from "../../variables/api/axiosInstance";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";

const { Dragger } = Upload;

// Régularisation des dossiers compagnies à partir des états SAP au 31/12/2025 (import Excel).
// Rapproche par numero_sinistrem, reconstruit éval = SAP + cumul(≤31/12/2025), pose SAP/statut. HSI115 (Pool).
const fmt = (n) => (n === null || n === undefined || n === "" ? "—" : Math.trunc(Number(n) || 0).toLocaleString("fr-FR"));

const RegularisationSap = () => {
    const [file, setFile] = useState(null);
    const [format, setFormat] = useState("POOL"); // POOL = auto-détection ; MANUEL = mapping
    const [map, setMap] = useState({ sheetIndex: 0, headerRow: 1, colNumero: 1, colSap: 13, colStatut: 15 });
    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [res, setRes] = useState(null);

    const buildForm = () => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("format", format);
        if (format === "MANUEL") {
            fd.append("sheetIndex", map.sheetIndex);
            fd.append("headerRow", map.headerRow);
            fd.append("colNumero", map.colNumero);
            fd.append("colSap", map.colSap);
            if (map.colStatut) fd.append("colStatut", map.colStatut);
        }
        return fd;
    };

    const call = async (url, setBusy) => {
        if (!file) { toast.warn("Sélectionnez d'abord un fichier."); return null; }
        setBusy(true);
        try {
            const r = await axiosInstance.post(url, buildForm(), { headers: { "Content-Type": "multipart/form-data" } });
            return r.data;
        } catch (e) {
            toast.error(e?.response?.data?.message || "Échec de l'opération.");
            return null;
        } finally { setBusy(false); }
    };

    const doPreview = async () => {
        const data = await call("/api/sinistre/sap-regularisation/preview", setLoading);
        if (data) {
            setRes(data);
            if (data.messageErreur) toast.error(data.messageErreur);
            else toast.success(`${data.nbRapproches}/${data.nbLignes} dossier(s) rapproché(s).`);
        }
    };

    const doApply = () => {
        Modal.confirm({
            title: "Appliquer la régularisation ?",
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p>Une <b>réévaluation « Régularisation SAP compagnie 31/12/2025 »</b> sera créée sur chaque
                        dossier rapproché, et l'<b>évaluation courante, le SAP et le statut</b> seront mis à jour.</p>
                    <p><b>{res?.nbRapproches || 0}</b> dossier(s) seront modifiés. Action irréversible.</p>
                </div>
            ),
            okText: "Appliquer", cancelText: "Annuler", okButtonProps: { danger: true },
            onOk: async () => {
                const data = await call("/api/sinistre/sap-regularisation/apply", setApplying);
                if (data) {
                    setRes(data);
                    toast.success(`${data.nbAppliques} dossier(s) régularisé(s).`);
                }
            },
        });
    };

    const draggerProps = {
        multiple: false,
        maxCount: 1,
        accept: ".xlsx,.xls",
        beforeUpload: (f) => { setFile(f); setRes(null); return Upload.LIST_IGNORE; },
        onRemove: () => { setFile(null); setRes(null); },
    };

    const columns = useMemo(() => [
        { title: "Ligne", dataIndex: "rowNum", width: 70 },
        { title: "N° sinistre (fichier)", dataIndex: "numeroSinistreFichier" },
        {
            title: "Rapproché", dataIndex: "rapproche", width: 110,
            render: (v, r) => v
                ? <Tag color="green">{r.numSinistre || "oui"}</Tag>
                : <Tag color="red">introuvable</Tag>,
        },
        {
            title: "Statut", key: "statut", width: 120,
            render: (_, r) => r.rapproche
                ? <span>{r.statutActuel || "—"} → <b>{r.statutCible || r.statutActuel || "—"}</b></span> : "—",
        },
        {
            title: "Évaluation", key: "eval", align: "right",
            render: (_, r) => r.rapproche ? <span>{fmt(r.evalAvant)} → <b>{fmt(r.evalCible)}</b></span> : "—",
        },
        {
            title: "SAP", key: "sap", align: "right",
            render: (_, r) => r.rapproche ? <span>{fmt(r.sapAvant)} → <b>{fmt(r.sapCible)}</b></span> : "—",
        },
        { title: "SAP fichier", dataIndex: "sapFichier", align: "right", render: (v) => fmt(v) },
        {
            title: "Avertissement", dataIndex: "avertissement",
            render: (v) => v ? <span style={{ color: "#ad6800" }}>{v}</span> : "",
        },
    ], []);

    const canApply = res && !res.messageErreur && res.nbRapproches > 0;

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"SINISTRES"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
            <h2>Régularisation SAP — états compagnies (31/12/2025)</h2>
            <Alert type="info" showIcon style={{ marginBottom: 12 }}
                message="Import des états SAP fournis par les compagnies"
                description="Rapprochement par « numéro de sinistre » (= numero_sinistrem). L'évaluation courante est reconstruite = SAP du fichier + cumul des règlements au 31/12/2025 ; le SAP et le statut sont mis à jour. Prévisualisez avant d'appliquer." />

            <Card size="small" style={{ marginBottom: 12 }}>
                <Dragger {...draggerProps}>
                    <p className="ant-upload-drag-icon"><UploadOutlined /></p>
                    <p className="ant-upload-text">Cliquez ou glissez le fichier Excel (.xlsx / .xls)</p>
                    {file && <p className="ant-upload-hint">Sélectionné : <b>{file.name}</b></p>}
                </Dragger>

                <Divider style={{ margin: "12px 0" }} />
                <Space wrap align="end">
                    <div>
                        <div style={{ fontSize: 12, color: "#888" }}>Format</div>
                        <Select value={format} onChange={(v) => setFormat(v)} style={{ width: 320 }}
                            options={[
                                { value: "POOL", label: "Auto-détection (FORMAT POOL / SUNU / SAP POOL)" },
                                { value: "SANLAM", label: "Sanlam (classeur multi-feuilles par année)" },
                                { value: "MANUEL", label: "Mapping manuel des colonnes" },
                            ]} />
                        {format === "SANLAM" && (
                            <div style={{ fontSize: 12, color: "#888", maxWidth: 320, marginTop: 2 }}>
                                Lit toutes les feuilles annuelles (TPV + 2 roues), colonne « MONTANT » = SAP ;
                                ignore les bandes, sous-totaux et les feuilles « Synthèses » / « Sinistres Clôturés ».
                            </div>
                        )}
                    </div>
                    {format === "MANUEL" && (
                        <>
                            {[["Feuille (0=1ère)", "sheetIndex"], ["Ligne en-tête", "headerRow"],
                            ["Col. N° sinistre", "colNumero"], ["Col. SAP", "colSap"], ["Col. statut", "colStatut"]]
                                .map(([lab, key]) => (
                                    <div key={key}>
                                        <div style={{ fontSize: 12, color: "#888" }}>{lab}</div>
                                        <InputNumber min={0} value={map[key]}
                                            onChange={(v) => setMap((m) => ({ ...m, [key]: v }))} style={{ width: 110 }} />
                                    </div>
                                ))}
                        </>
                    )}
                    <Button type="primary" loading={loading} onClick={doPreview} disabled={!file}>Prévisualiser</Button>
                    <Button danger loading={applying} onClick={doApply} disabled={!canApply}>Appliquer</Button>
                </Space>
            </Card>

            {res && !res.messageErreur && (
                <Row gutter={12} style={{ marginBottom: 12 }}>
                    <Col><Card size="small"><Statistic title="Lignes" value={res.nbLignes} /></Card></Col>
                    <Col><Card size="small"><Statistic title="Rapprochés" value={res.nbRapproches} valueStyle={{ color: "#3f8600" }} /></Card></Col>
                    <Col><Card size="small"><Statistic title="Introuvables" value={res.nbIntrouvables} valueStyle={{ color: "#cf1322" }} /></Card></Col>
                    <Col><Card size="small"><Statistic title="Avertissements" value={res.nbAvertissements} valueStyle={{ color: "#ad6800" }} /></Card></Col>
                    {res.nbAppliques > 0 && <Col><Card size="small"><Statistic title="Appliqués" value={res.nbAppliques} valueStyle={{ color: "#1677ff" }} /></Card></Col>}
                </Row>
            )}

            {res?.messageErreur && <Alert type="error" showIcon message={res.messageErreur} style={{ marginBottom: 12 }} />}

            {res && !res.messageErreur && (
                <Table rowKey={(r) => `${r.rowNum}-${r.numeroSinistreFichier}`} size="small"
                    columns={columns} dataSource={res.lignes} pagination={{ pageSize: 50, showSizeChanger: true }}
                    scroll={{ x: 1100 }} />
            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegularisationSap;
